import { type Filter } from '@spendesk/bookkeeping-core-types';
import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { type FiltersState } from 'modules/bookkeep/payables/containers/PayablesFiltersContainer/hooks/useFiltersContext';
import { useQuery } from 'src/core/api/hooks/useQuery';
import { type QueryState } from 'src/core/api/queryState';
import {
  withoutOptionalFields,
  type FilterWithOptionalField,
} from 'src/core/common/components/FilterBuilder';
import { logger } from 'src/utils/datadog-log-wrapper';

import { type RawResult, PAYABLES_COUNT_QUERY } from './query';
import { toCoreTypeFilter } from '../payable-filter/payableFilterTransformer';
import { getPayableCountQueryKey } from '../query-key-registry';

export const useInvalidateGetPayableCountQueryCache = () => {
  const queryClient = useQueryClient();

  return async (companyId: string): Promise<void> => {
    await queryClient.invalidateQueries(getPayableCountQueryKey(companyId));
  };
};

/**
 * @public
 */
export type PayableCountResponse = { totalCount: number };

export type PayableCountRawResponse = RawResult;

export type PayableCountVariables = {
  companyId: string;
  filtersV2: Filter | undefined;
  textualSearch: string | undefined;
} & FiltersState;

export const useGetPayableCount = ({
  filters,
  filtersV2: rawFiltersV2,
}: {
  filters: FiltersState;
  filtersV2: FilterWithOptionalField | undefined;
}): QueryState<PayableCountResponse> => {
  const companyId = useCompanyId();

  const filtersV2 = rawFiltersV2
    ? toCoreTypeFilter(withoutOptionalFields(rawFiltersV2))
    : undefined;

  const variables: PayableCountVariables = {
    companyId,
    search: filters.search,
    filtersV2,
    textualSearch: filters.search,
  };

  return useQuery<PayableCountResponse, RawResult>({
    key: getPayableCountQueryKey(companyId, filters.search, filtersV2),
    request: {
      type: 'graphQL',
      target: 'v2',
      query: PAYABLES_COUNT_QUERY,
      variables,
    },
    reshapeData: (data) => {
      if (!data.company?.payables) {
        logger.warn('data from getPayableCount is not shaped as expected', {
          scope: 'payable',
          team: 'finance-accountant',
          data,
        });
      }
      return {
        totalCount: data.company?.payables?.totalCount ?? 0,
      };
    },
  });
};
